import {
	Box,
	Button,
	HStack,
	Icon,
	IconButton,
	Link,
	Text,
	Tooltip,
	VStack
} from '@chakra-ui/react'
import { LANGUAGES } from 'commons/constants/language'
import { PaymentSources } from 'commons/constants/paymentTypes'
import { BgColors, BrandColorsRaw, Spacing, TextColors } from 'commons/design-system/tokens'
import { useCreateAISRequest } from 'commons/hooks/services/useCreateAISRequest'
import { CopyIcon } from 'features/success-page/components/icons/CopyIcon'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { LuInfo } from 'react-icons/lu'

type Props = {
	iban: string
	name: string
	bic: string
	identifier: string
	amount: number
	source: PaymentSources
}

export const IbanField = ({ iban, name, bic, identifier, amount, source }: Props) => {
	const { t, i18n } = useTranslation()
	const [copied, setCopied] = useState(false)
	const createAISRequestMutation = useCreateAISRequest(source)
	const locale = i18n.language === LANGUAGES.FRENCH ? 'fr_FR' : 'en_US'

	const learnMoreUrl = useMemo(() => {
		if (source === PaymentSources.MONEY_POT) {
			if (i18n.language === LANGUAGES.FRENCH) {
				return 'https://support.lydia.me/l/fr/article/hjvtzyszge-propos-des-cagnottes-lydia#:~:text=des%20cartes%20accept%C3%A9es-,Par%20virement,-Faire%20un%20virement'
			}
			return 'https://support.lydia.me/l/en/article/hjvtzyszge-about-lydia-money-pots#:~:text=of%20accepted%20cards-,By%20wire%20transfer,-Perform%20an%20instant'
		} else {
			if (i18n.language === LANGUAGES.FRENCH) {
				return 'https://support.lydia.me/l/fr/article/8ikppivi7m-ma-page-lydia'
			}
			return 'https://support.lydia.me/l/en/article/8ikppivi7m-my-lydia-webpage'
		}
	}, [source])

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(iban.replace(/\s/g, ''))
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			// Fallback for older browsers
			const textArea = document.createElement('textarea')
			textArea.value = iban.replace(/\s/g, '')
			document.body.appendChild(textArea)
			textArea.select()
			document.execCommand('copy')
			document.body.removeChild(textArea)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		}
	}

	const handleCreateAISRequest = () => {
		const redirectUrl =
			source === PaymentSources.MONEY_POT ? 'successTinkPayment' : 'successTinkPaymentLydiaPage'
		const newWindow = window.open('about:blank', '_blank')
		createAISRequestMutation.mutate(
			{
				source,
				identifier,
				amount,
				iban: iban.replace(/\s/g, ''),
				recipientName: name
			},
			{
				onSuccess: (data) => {
					if (data.request_id && newWindow) {
						newWindow.location.href = `https://link.tink.com/1.0/pay/direct?client_id=${process.env.REACT_APP_TINK_CLIENT_ID}&market=FR&locale=${locale}&payment_request_id=${data.request_id}&redirect_uri=${process.env.REACT_APP_POTS_URL}collect/${redirectUrl}`
					} else {
						newWindow?.close()
					}
				},
				onError: () => {
					newWindow?.close()
					window.location.href = `${process.env.REACT_APP_POTS_URL}collect/${
						source === PaymentSources.MONEY_POT ? 'errorTinkPayment' : 'errorTinkPaymentLydiaPage'
					}`
				}
			}
		)
	}

	return (
		<Box bg='white' borderRadius='md' w='full' maxW='600px' p={Spacing.XL}>
			<VStack align='stretch' gap={Spacing.M}>
				<VStack align='center' gap={Spacing.S}>
					<Button
						disabled={amount <= 0}
						onClick={handleCreateAISRequest}
						fontFamily='Montserrat'
						fontWeight={700}
						fontSize='15px'
						lineHeight='18px'
						bg={BrandColorsRaw.PRIMARY}
						color={TextColors.OPPOSITE_PRIMARY}
						borderRadius='full'
						px={6}
						py={5}
						gap={Spacing.XS}
						_hover={{
							bg: BgColors.OPPOSITE_Z2
						}}
					>
						<FaExternalLinkAlt color={TextColors.OPPOSITE_PRIMARY} size={12} />
						{source === PaymentSources.MONEY_POT
							? t('money_pot.participate_with_bank')
							: t('lydia_page.participate_with_bank')}
					</Button>
					<Text
						fontFamily='Montserrat'
						fontWeight={500}
						fontSize='14px'
						color={TextColors.SECONDARY}
						textAlign='center'
						lineHeight='20px'
						maxW='370px'
					>
						{t('money_pot.redirect_to_bank')}{' '}
						<Link
							href={learnMoreUrl}
							target='_blank'
							rel='noopener noreferrer'
							color={BrandColorsRaw.PRIMARY}
							textDecoration='underline'
						>
							{t('money_pot.learn_more')}
						</Link>
					</Text>
				</VStack>
				<Text
					fontFamily='Montserrat'
					fontWeight={700}
					textAlign='center'
					fontSize='16px'
					color={TextColors.PRIMARY}
					lineHeight='18px'
				>
					{t('money_pot.or')}
				</Text>
				<Text
					fontFamily='Montserrat'
					fontSize='16px'
					color={TextColors.PRIMARY}
					lineHeight='22px'
					textAlign='center'
					maxW='410px'
					alignSelf='center'
					fontWeight={600}
				>
					{source === PaymentSources.MONEY_POT
						? t('money_pot.moneypot_has_iban_instruction')
						: t('lydia_page.has_iban_instruction')}
				</Text>
				<Box bg={BgColors.NEUTRAL_Z0} borderRadius='lg' p={Spacing.M}>
					<VStack align='stretch' gap={Spacing.S}>
						<HStack justify='space-between' align='center'>
							<Text
								fontFamily='Montserrat'
								fontWeight={600}
								fontSize='16px'
								color={TextColors.PRIMARY}
							>
								{name}
							</Text>
							<Tooltip.Root positioning={{ placement: 'top' }}>
								<Tooltip.Trigger asChild>
									<Box as='span' cursor='pointer'>
										<Icon as={LuInfo} boxSize='18px' color={TextColors.TERTIARY} />
									</Box>
								</Tooltip.Trigger>
								<Tooltip.Positioner>
									<Tooltip.Content>
										<Tooltip.Arrow />
										{source === PaymentSources.MONEY_POT
											? t('money_pot.iban_info')
											: t('lydia_page.iban_info')}
									</Tooltip.Content>
								</Tooltip.Positioner>
							</Tooltip.Root>
						</HStack>
						<HStack gap={Spacing.XS} align='center' height='16px'>
							<Text
								fontFamily='Montserrat'
								fontWeight={600}
								fontSize='14px'
								color={TextColors.SECONDARY}
							>
								IBAN : {iban}
							</Text>
							<IconButton
								aria-label={t('success.share_link_copied')}
								onClick={handleCopy}
								size='sm'
								variant='ghost'
								color={TextColors.SECONDARY}
								_hover={{ bg: '#E2E8F0' }}
								minW='auto'
								h='auto'
								p='3px'
							>
								<CopyIcon color={copied ? TextColors.STATUS_SUCCESS : TextColors.SECONDARY} />
							</IconButton>
						</HStack>
						<Text
							fontFamily='Montserrat'
							fontWeight={600}
							fontSize='14px'
							lineHeight='16px'
							color={TextColors.SECONDARY}
						>
							BIC : {bic}
						</Text>
						<Text
							fontFamily='Montserrat'
							fontWeight={600}
							lineHeight='16px'
							fontSize='14px'
							color={TextColors.SECONDARY}
						>
							{t('phone_form.domiciliation')} : Lydia Solutions, 14 avenue de l'Opéra, 75001 Paris
						</Text>
					</VStack>
				</Box>
			</VStack>
		</Box>
	)
}
